import { getApiUrl } from './endpoints';

const ACCESS_KEY = 'auth_token';
const REFRESH_KEY = 'auth_refresh_token';

export interface AuthTokens {
  token?: string;
  refreshToken?: string;
}

/** Session-expired signal for the UI. Never navigates by itself. */
export const SESSION_EXPIRED_EVENT = 'app:sessionExpired';

export function saveAuthSession(data: AuthTokens): void {
  try {
    if (data.token) localStorage.setItem(ACCESS_KEY, data.token);
    if (data.refreshToken) localStorage.setItem(REFRESH_KEY, data.refreshToken);
  } catch {
    // ignore storage errors (private mode / restricted environments)
  }
}

export function clearAuthSession(): void {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    // ignore
  }
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY);
  } catch {
    return null;
  }
}

export function hasRefreshToken(): boolean {
  try {
    return !!localStorage.getItem(REFRESH_KEY);
  } catch {
    return false;
  }
}

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Mint a new access token from the stored refresh token.
 *
 * Single-flight: the server *rotates* the refresh token (the old session is
 * deleted when a new pair is issued), so parallel refreshes would invalidate
 * each other — concurrent 401s share one attempt instead.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      let refreshToken: string | null = null;
      try {
        refreshToken = localStorage.getItem(REFRESH_KEY);
      } catch {
        return null;
      }
      if (!refreshToken) return null;

      const res = await fetch(`${getApiUrl()}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return null;

      const data = await res.json();
      if (!data?.token) return null;

      saveAuthSession(data); // rotation: keep the new access *and* refresh token
      return data.token as string;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

/**
 * Fetch with the session token, refreshing silently on 401.
 *
 * Contract: this never navigates. If the refresh also fails it returns the 401
 * response and dispatches `app:sessionExpired`, so the caller can show a
 * non-destructive prompt — an expired session must not yank a user who is
 * mid-typing or mid-recording (see docs/TROUBLESHOOTING.md §2).
 *
 * Do not use for `/api/auth/login` or `/api/auth/refresh` themselves.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const withToken = (token: string | null, base: RequestInit): RequestInit => ({
    ...base,
    headers: {
      ...((base.headers as Record<string, string> | undefined) ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  let res = await fetch(input, withToken(getAccessToken(), init));

  if (res.status === 401) {
    const token = await refreshAccessToken();
    if (token) {
      res = await fetch(input, withToken(token, init));
    }
  }

  if (res.status === 401) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  }

  return res;
}
